import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextField, Alert, Box } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { signUp } from "aws-amplify/auth";
import { useAuth } from "../libs/AuthContext";
import LoaderButton from "../components/LoaderButton";
import FormPage from "../components/FormPage";

const schema = Yup.object({
  Email: Yup.string()
    .required("Email is required")
    .matches(
      /^[a-zA-Z0-9.!#$%&’'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
      "Invalid email address"
    ),
  Password: Yup.string()
    .required("Password is required")
    .min(8, "Password too short — minimum 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      "Must contain lowercase, uppercase, number, and special character."
    ),
  ConfirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("Password")], "Passwords don't match")
});

export default function Signup() {
  const { emailAddressHandler } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const handleSubmit = async (event) => {
    setIsLoading(true);
    setAlertMessage("");
    try {
      await signUp({
        username: event.Email.toLowerCase(),
        password: event.Password,
        options: { userAttributes: { email: event.Email.toLowerCase() } }
      });
      setIsLoading(false);
      emailAddressHandler(event.Email);
      navigate("/login/verify");
    } catch (e) {
      setAlertMessage(e?.message ?? "Signup failed");
      setIsLoading(false);
    }
  };

  return (
    <FormPage
      title="Create your account"
      subtitle="Registration links you to your organisation's licensed seat. If your organisation isn't registered, contact sales@absolute.technology."
    >
      {alertMessage ? (
        <Alert severity="error" onClose={() => setAlertMessage("")}>{alertMessage}</Alert>
      ) : null}
      <Formik
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{ Email: "", Password: "", ConfirmPassword: "" }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                name="Email"
                label="Work email"
                type="email"
                value={values.Email}
                onChange={handleChange}
                error={!!errors.Email}
                helperText={errors.Email}
                autoComplete="email"
                autoFocus
              />
              <TextField
                name="Password"
                label="Password"
                type="password"
                value={values.Password}
                onChange={handleChange}
                error={!!errors.Password}
                helperText={errors.Password ?? "Min 8 chars · 1 upper · 1 lower · 1 number · 1 symbol"}
                autoComplete="new-password"
              />
              <TextField
                name="ConfirmPassword"
                label="Confirm password"
                type="password"
                value={values.ConfirmPassword}
                onChange={handleChange}
                error={!!errors.ConfirmPassword}
                helperText={errors.ConfirmPassword}
                autoComplete="new-password"
              />
              <LoaderButton
                type="submit"
                isLoading={isLoading}
                text="Create account"
                loadingText="Registering…"
                fullWidth
                size="large"
              />
            </Stack>
          </Box>
        )}
      </Formik>
    </FormPage>
  );
}
